import { useState, useEffect } from "react";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import {
  ISheetConfigResponse,
  IUpdateSheetConfigRequest,
  IUpdateSheetConfigResponse,
} from "@/utilities/types/AgencyIntegration";
import { E_AGENCY_INTEGRATION } from "@/utilities/enums/AgencyIntegration";

interface UseSheetConfigReturn {
  config: ISheetConfigResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseUpdateSheetConfigReturn {
  updateConfig: (
    request: IUpdateSheetConfigRequest
  ) => Promise<IUpdateSheetConfigResponse>;
  isUpdating: boolean;
  error: string | null;
}

export function useSheetConfig(): UseSheetConfigReturn {
  const [config, setConfig] = useState<ISheetConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authHeader = useAuthHeader();

  const fetchConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!authHeader) {
        throw new Error("No authentication token available");
      }

      const response = await fetch(
        `${import.meta.env.VITE_BASE_API_URL}${E_AGENCY_INTEGRATION.SHEET_CONFIG}`,
        {
          headers: {
            Authorization: authHeader,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          setConfig(null);
          return;
        }
        throw new Error(
          `Failed to fetch sheet configuration: ${response.statusText}`
        );
      }

      const responseData = await response.json();
      const data: ISheetConfigResponse = responseData.data || responseData;
      setConfig(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to fetch sheet configuration";
      setError(errorMessage);
      setConfig(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  return {
    config,
    isLoading,
    error,
    refetch: fetchConfig,
  };
}

export function useUpdateSheetConfig(): UseUpdateSheetConfigReturn {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authHeader = useAuthHeader();

  const updateConfig = async (request: IUpdateSheetConfigRequest) => {
    try {
      setIsUpdating(true);
      setError(null);

      if (!authHeader) {
        throw new Error("No authentication token available");
      }

      const response = await fetch(
        `${import.meta.env.VITE_BASE_API_URL}${E_AGENCY_INTEGRATION.SHEET_CONFIG}`,
        {
          method: "PUT",
          headers: {
            Authorization: authHeader,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name:
              request.name === undefined
                ? undefined
                : request.name === null
                  ? null
                  : request.name,
            folderId:
              request.folderId === undefined
                ? undefined
                : request.folderId === null
                  ? null
                  : request.folderId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = `Failed to update configuration: ${response.statusText}`;

        if (response.status === 400) {
          errorMessage = errorData.message || "Invalid configuration data";
        } else if (response.status === 403) {
          errorMessage = "You do not have access to the specified folder";
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }

        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      const apiData = responseData.data || responseData;
      const data: IUpdateSheetConfigResponse = {
        message: responseData.message || "Configuration updated successfully",
        config: apiData.config || {},
      };
      return data;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to update sheet configuration";
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateConfig,
    isUpdating,
    error,
  };
}
